import type { FC, KeyboardEvent, ReactNode } from 'react'

import { useEffect, useId, useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AppIcon } from '@/components/generals/app-icon'
import { shortName } from '@/lib/workflows'

interface AppSelectProps {
    label: string
    value: string
    options: Array<string>
    disabledOption?: string
    onChange: (value: string) => void
}

// Pill-shaped dropdown that shows each app with its icon. Supports arrow keys, Enter, Escape and type-to-select.
export const AppSelect: FC<AppSelectProps> = ({ label, value, options, disabledOption, onChange }): ReactNode => {
    const id = useId()
    const [open, setOpen] = useState(false)
    const [highlight, setHighlight] = useState(Math.max(0, options.indexOf(value)))
    const rootRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLUListElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (!open) return

        const handleClick = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
        }

        document.addEventListener('mousedown', handleClick)
        listRef.current?.focus()

        return () => document.removeEventListener('mousedown', handleClick)
    }, [open])

    useEffect(() => {
        if (!open) return

        listRef.current?.querySelector<HTMLElement>(`[data-index="${highlight}"]`)?.scrollIntoView({ block: 'nearest' })
    }, [highlight, open])

    const isDisabled = (option: string) => option === disabledOption

    const move = (direction: 1 | -1) => {
        let next = highlight

        for (let step = 0; step < options.length; step += 1) {
            next = (next + direction + options.length) % options.length

            if (!isDisabled(options[next])) break
        }

        setHighlight(next)
    }

    const choose = (option: string) => {
        if (isDisabled(option)) return

        onChange(option)
        setOpen(false)
        buttonRef.current?.focus()
    }

    const openList = () => {
        setHighlight(Math.max(0, options.indexOf(value)))
        setOpen(true)
    }

    const handleListKeys = (event: KeyboardEvent<HTMLUListElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            move(1)
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault()
            move(-1)
        }
        else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            choose(options[highlight])
        }
        else if (event.key === 'Escape' || event.key === 'Tab') {
            setOpen(false)
            buttonRef.current?.focus()
        }
        else if (event.key.length === 1) {
            const match = options.findIndex(option => option.toLowerCase().startsWith(event.key.toLowerCase()) && !isDisabled(option))

            if (match >= 0) setHighlight(match)
        }
    }

    return (
        <div ref={rootRef} className='relative w-full sm:w-[280px]'>
            <button
                ref={buttonRef}
                type='button'
                aria-haspopup='listbox'
                aria-expanded={open}
                aria-controls={`${id}-list`}
                aria-label={`${label}: ${value}`}
                onClick={() => (open ? setOpen(false) : openList())}
                onKeyDown={event => {
                    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                        event.preventDefault()
                        openList()
                    }
                }}
                className='flex h-14 w-full items-center gap-3 rounded-full bg-white pl-2.5 pr-5 text-left shadow-[0_1px_2px_rgba(14,14,14,0.04),0_10px_24px_-16px_rgba(14,14,14,0.25)] ring-1 ring-[#e6e6ec] transition-shadow hover:ring-[#d6d6de] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet'
            >
                <AppIcon name={value} size='md' shape='circle' />

                <span className='flex min-w-0 flex-1 flex-col'>
                    <span className='text-[11.5px] font-medium text-silver'>
                        {label}
                    </span>

                    <span className='truncate text-[15px] font-semibold tracking-[-0.01em] text-ink'>
                        {shortName(value)}
                    </span>
                </span>

                <ChevronDown aria-hidden='true' className={cn('size-4 shrink-0 text-slate transition-transform duration-200', open && 'rotate-180')} />
            </button>

            {open && (
                <ul
                    ref={listRef}
                    id={`${id}-list`}
                    role='listbox'
                    tabIndex={-1}
                    aria-label={label}
                    aria-activedescendant={`${id}-option-${highlight}`}
                    onKeyDown={handleListKeys}
                    className='fc-pop-in absolute left-0 right-0 top-[calc(100%+8px)] z-30 max-h-[300px] overflow-y-auto rounded-[18px] bg-white p-1.5 shadow-[0_24px_50px_-24px_rgba(14,14,14,0.35),0_0_0_1px_rgba(14,14,14,0.08)] focus:outline-none'
                >
                    {options.map((option, index) => {
                        const disabled = isDisabled(option)
                        const selected = option === value

                        return (
                            <li
                                key={option}
                                id={`${id}-option-${index}`}
                                data-index={index}
                                role='option'
                                aria-selected={selected}
                                aria-disabled={disabled}
                                onMouseEnter={() => !disabled && setHighlight(index)}
                                onClick={() => choose(option)}
                                className={cn(
                                    'flex h-11 cursor-pointer items-center gap-3 rounded-[12px] px-2.5 text-[14px] font-medium text-ink',
                                    index === highlight && !disabled && 'bg-mist',
                                    disabled && 'cursor-not-allowed opacity-40'
                                )}
                            >
                                <AppIcon name={option} size='sm' shape='circle' />

                                <span className='flex-1 truncate'>
                                    {shortName(option)}
                                </span>

                                {selected && <Check aria-hidden='true' className='size-4 text-violet' />}
                            </li>
                        )
                    })}
                </ul>
            )}
        </div>
    )
}
